// src/pages/form/forms/ApplyIndividualLiteContent.js
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Logo from "../../../assets/images/LogoNew.svg";
import TxnLimit from "../../../assets/images/mobilefeature.svg";
import Money from "../../../assets/images/money.svg";
import MobileFeature from "../../../assets/images/peron.svg";
import PersonConfig from "../../../assets/images/personconfig.svg";
import Person from "../../../assets/images/txnlimit.svg";
import { buildAutomationChildTestId } from '../../../components/InputField/testId';
import LanguagePicker from '../../../components/LanguagePicker/LanguagePicker';
import { useTranslation } from '../../../store/context/TranslationContext';
import ButtonGroupNew from './BusinessLiteForms/components/ButtonGroupNew';
import './IndividualLite.scss'; // Ensure the SCSS file is correctly imported

const INDIVIDUAL_PLUS_LANDING_TEST_ID = "individual-plus-landing";

const FEATURE_ITEMS = [
    {
        key: "simple-banking",
        icon: Person,
        text: "Ideal for individuals seeking a simple banking solution for personal use.",
    },
    {
        key: "low-fees",
        icon: Money,
        text: "Offers reduced or no monthly maintenance fees to keep banking affordable.",
    },
    {
        key: "digital-access",
        icon: MobileFeature,
        text: "Includes basic features such as online banking, a debit card, and mobile app access.",
    },
    {
        key: "transaction-limits",
        icon: TxnLimit,
        text: "May include transaction limits suitable for average personal use, reducing costs and complexity.",
    },
    {
        key: "streamlined-application",
        icon: PersonConfig,
        text: "Streamlined application process and user-friendly account management tools.",
    },
];

const PASSPORT_ACKNOWLEDGEMENT_TEXT = "I have my valid passport ready and understand that any application fee paid is non-refundable.";
const PASSPORT_ACKNOWLEDGEMENT_INPUT_ID = "individual-plus-passport-acknowledgement";

const ApplyIndividualLiteContent = () => {
    const navigate = useNavigate();
    const { t } = useTranslation();

    // Applicants must explicitly acknowledge the passport / non-refundable-fee disclaimer before
    // they can start the application. Gate only - nothing is persisted.
    const [hasAcknowledged, setHasAcknowledged] = useState(false);

    const currentUrl = window.location.href;
    const urlObject = new URL(currentUrl);
    const queryParams = urlObject.search; // Get the query string

    const acknowledgementTestId = buildAutomationChildTestId(INDIVIDUAL_PLUS_LANDING_TEST_ID, "passport-acknowledgement");

    const handleGetStarted = () => {
        // Defence in depth: the button is disabled until acknowledged, but never navigate without it.
        if (!hasAcknowledged) {
            return;
        }
        navigate(`/individual-plus/login${queryParams}`);
    };

    return (
        <div className="form-content-container" data-testid={INDIVIDUAL_PLUS_LANDING_TEST_ID}>

            <div className='form-content-header' data-testid={buildAutomationChildTestId(INDIVIDUAL_PLUS_LANDING_TEST_ID, "header")}>
                <img
                    src={Logo}
                    alt={t("fv-bank")}
                    className="logo logo-dark form-content-logo"
                    data-testid={buildAutomationChildTestId(INDIVIDUAL_PLUS_LANDING_TEST_ID, "logo")}
                />
                <div className='' data-testid={buildAutomationChildTestId(INDIVIDUAL_PLUS_LANDING_TEST_ID, "language-wrap")}><LanguagePicker testId={buildAutomationChildTestId(INDIVIDUAL_PLUS_LANDING_TEST_ID, "language")} /></div>

            </div>

            <div className="form-content-body with-shadow compact form-content-tabs " data-testid={buildAutomationChildTestId(INDIVIDUAL_PLUS_LANDING_TEST_ID, "card")}>
                <div className='form-content-body-header' data-testid={buildAutomationChildTestId(INDIVIDUAL_PLUS_LANDING_TEST_ID, "body-header")}>
                    <div className="" data-testid={buildAutomationChildTestId(INDIVIDUAL_PLUS_LANDING_TEST_ID, "title")}>{t('Individual Plus')}</div>
                </div>
                <div className='form-content-body-content' data-testid={buildAutomationChildTestId(INDIVIDUAL_PLUS_LANDING_TEST_ID, "content")}>
                    <ul className="fv_indi-list" data-testid={buildAutomationChildTestId(INDIVIDUAL_PLUS_LANDING_TEST_ID, "feature-list")}>
                        {FEATURE_ITEMS.map((item) => {
                            const itemTestId = buildAutomationChildTestId(INDIVIDUAL_PLUS_LANDING_TEST_ID, `feature-${item.key}`);
                            return (
                                <li className="fv_indi-item" key={item.key} data-testid={itemTestId}>
                                    <div className='fv_indi-icon-container' data-testid={buildAutomationChildTestId(itemTestId, "icon-wrap")}>
                                        <img
                                            src={item.icon}
                                            alt="fv-bank"
                                            data-testid={buildAutomationChildTestId(itemTestId, "icon")}
                                        />
                                    </div>
                                    <div className='' data-testid={buildAutomationChildTestId(itemTestId, "text")}>{t(item.text)}</div>
                                </li>
                            );
                        })}
                    </ul>
                    {/* Deliberately NOT using bootstrap's `.form-check` wrapper: it floats the input and
                        applies `margin-left: -1.5em`, which cancels the flex gap and jams the label against
                        the box. The `form-check-input` / `form-check-label` classes are kept purely for
                        their existing styling (20x20, black border). */}
                    <div className="fv_indi-acknowledgement mt-4" data-testid={acknowledgementTestId}>
                        <input
                            type="checkbox"
                            className="form-check-input"
                            id={PASSPORT_ACKNOWLEDGEMENT_INPUT_ID}
                            checked={hasAcknowledged}
                            onChange={(event) => setHasAcknowledged(event.target.checked)}
                            data-testid={buildAutomationChildTestId(acknowledgementTestId, "input")}
                        />
                        <label
                            className="form-check-label"
                            htmlFor={PASSPORT_ACKNOWLEDGEMENT_INPUT_ID}
                            data-testid={buildAutomationChildTestId(acknowledgementTestId, "label")}
                        >
                            {t(PASSPORT_ACKNOWLEDGEMENT_TEXT)}
                        </label>
                    </div>

                    <div className='mt-4' data-testid={buildAutomationChildTestId(INDIVIDUAL_PLUS_LANDING_TEST_ID, "actions")}>
                        <ButtonGroupNew
                            onNextClick={handleGetStarted}
                            step={2}
                            nextText={t("Get Started")}
                            asLabel={false}
                            loading={false}
                            isDisabled={!hasAcknowledged}
                            hideBackButton
                            testId={buildAutomationChildTestId(INDIVIDUAL_PLUS_LANDING_TEST_ID, "navigation")}
                            primaryActionTestIdSuffix="get-started"
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ApplyIndividualLiteContent;
